import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import { ContactDialog, ENTRY_CSS, EntryFooter, EntryHeader, Icon, MaroonDrift } from "../components/AugustEntry";

const ASSURANCES = ["Secure sign-in for every role", "One shared resume book", "RSVPs and preferences on record"];
const HEADLINE = ["August", "Board", "Meeting"];
const ACCENT_LINE = ["Trading,", "Risk", "&", "Investments", "Program"];

/** The three portals: each opens the sign-in page set up for that role. */
const DOORS = [
  { id: "student", icon: Icon.User, label: "Student Portal", description: "Submit your profile, resume and headshot, then complete your ranking and personal statement." },
  { id: "board", icon: Icon.Users, label: "Board Member Portal", description: "Review the resume book, RSVP for board week events and submit your top candidates." },
  { id: "admin", icon: Icon.Shield, label: "Admin Portal", description: "Manage accounts, generate resume books and run interview scheduling." },
];

const TICKER = ["Profiles", "Resumes", "Headshots", "Resume book", "Board week RSVPs", "Candidate preferences", "Interview scheduling"];

const STEPS = [
  { icon: Icon.Profile, title: "Student profiles", body: "Students submit their profile, resume and headshot, with a personal statement for the book." },
  { icon: Icon.Book, title: "Resume book", body: "Administrators compile every submission into the board's resume book." },
  { icon: Icon.Calendar, title: "Board week RSVPs", body: "Board members RSVP for the events of board week from their own portal." },
  { icon: Icon.List, title: "Preferences", body: "Board members pick their top candidates; students complete their rankings." },
  { icon: Icon.Clock, title: "Interview schedule", body: "Administrators turn both sides' preferences into the interview schedule." },
];

const LANDING_CSS = `
  .al-hero { position: relative; isolation: isolate; overflow: hidden; }
  .al-grid { display: grid; grid-template-columns: minmax(0,1fr); align-items: center; gap: 48px; padding-top: 128px; padding-bottom: 80px; }
  @media (min-width: 640px) { .al-grid { padding-top: 144px; padding-bottom: 96px; } }
  @media (min-width: 1024px) { .al-grid { grid-template-columns: minmax(0,1.15fr) minmax(0,1fr); gap: 64px; padding-top: 160px; padding-bottom: 128px; } }
  .al-pill { display: inline-flex; align-items: center; gap: 8px; border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.05); border-radius: 999px; padding: 4px 12px; font-size: 12.5px; font-weight: 500; color: rgba(255,255,255,0.75); backdrop-filter: blur(8px); }
  .al-dot { position: relative; width: 6px; height: 6px; }
  .al-dot::before, .al-dot::after { content: ""; position: absolute; inset: 0; border-radius: 50%; background: var(--ae-red); }
  .al-dot::before { animation: alPing 1.6s cubic-bezier(0,0,.2,1) infinite; opacity: .6; }
  @keyframes alPing { 75%, 100% { transform: scale(2.4); opacity: 0; } }
  .al-h1 { margin: 28px 0 0; font-size: clamp(2.3rem, 1.5rem + 2.4vw, 3.3rem); line-height: 1.06; font-weight: 600; letter-spacing: -0.03em; text-wrap: balance; }
  .al-h1 .line { display: block; }
  .al-lede { margin: 24px 0 0; max-width: 36rem; font-size: 16.5px; line-height: 1.65; color: rgba(255,255,255,0.65); }
  .al-assure { list-style: none; margin: 36px 0 0; padding: 0; display: flex; flex-direction: column; gap: 12px; font-size: 14px; color: rgba(255,255,255,0.85); }
  @media (min-width: 640px) { .al-assure { flex-direction: row; flex-wrap: wrap; column-gap: 24px; } }
  .al-assure li { display: flex; align-items: center; gap: 8px; }
  .al-check { width: 20px; height: 20px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 1px solid rgba(229,72,77,0.45); background: rgba(229,72,77,0.12); color: var(--ae-rose); }

  .al-panel { position: relative; border-radius: 16px; border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.04); padding: 24px; backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px);
              box-shadow: 0 40px 80px -30px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.06); }
  @media (min-width: 640px) { .al-panel { padding: 28px; } }
  .al-panel-hair { position: absolute; left: 40px; right: 40px; top: -1px; height: 1px; background: linear-gradient(90deg, transparent, rgba(229,72,77,0.85), transparent); }
  .al-panel-eyebrow { display: flex; align-items: center; gap: 8px; font-size: 12px; font-weight: 500; letter-spacing: 0.12em; text-transform: uppercase; color: var(--ae-rose); }
  .al-panel h2 { margin: 8px 0 0; font-size: 20px; font-weight: 600; letter-spacing: -0.01em; }
  .al-panel > p { margin: 4px 0 0; font-size: 14px; color: rgba(255,255,255,0.6); }
  .al-doors { margin-top: 24px; display: grid; gap: 12px; }
  .al-door { position: relative; display: flex; align-items: center; gap: 16px; overflow: hidden; width: 100%; text-align: left; font: inherit; color: inherit; cursor: pointer;
             border-radius: 12px; border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.03); padding: 16px; transition: transform .3s, border-color .3s, background .3s, box-shadow .3s; }
  .al-door:hover { transform: translateY(-2px); border-color: rgba(229,72,77,0.55); background: rgba(255,255,255,0.07); box-shadow: 0 16px 40px -16px rgba(229,72,77,0.45); }
  .al-door:focus-visible { outline: 2px solid var(--ae-rose); outline-offset: 2px; }
  .al-door .shine { position: absolute; top: 0; bottom: 0; left: -50%; width: 50%; transform: skewX(-12deg); background: linear-gradient(90deg, transparent, rgba(255,255,255,0.07), transparent); transition: transform .7s; pointer-events: none; }
  .al-door:hover .shine { transform: translateX(300%) skewX(-12deg); }
  .al-door .ico { width: 40px; height: 40px; flex: none; display: flex; align-items: center; justify-content: center; border-radius: 8px; border: 1px solid rgba(255,255,255,0.1);
                  background: linear-gradient(180deg, #8f2a3c, #500000); box-shadow: inset 0 1px 0 rgba(255,255,255,0.15); transition: background .3s; }
  .al-door:hover .ico { background: linear-gradient(180deg, #ff5a60, #b80000); }
  .al-door .txt { min-width: 0; flex: 1; }
  .al-door .txt b { display: block; font-size: 15px; font-weight: 600; }
  .al-door .txt span { display: block; margin-top: 2px; font-size: 13px; line-height: 1.4; color: rgba(255,255,255,0.55); }
  .al-door .arr { flex: none; color: rgba(255,255,255,0.4); transition: transform .3s, color .3s; }
  .al-door:hover .arr { transform: translateX(4px); color: var(--ae-rose); }
  .al-panel-foot { margin-top: 24px; padding-top: 16px; border-top: 1px solid rgba(255,255,255,0.1); display: flex; flex-direction: column; gap: 4px; font-size: 12.5px; color: rgba(255,255,255,0.5); }
  @media (min-width: 640px) { .al-panel-foot { flex-direction: row; justify-content: space-between; align-items: center; } }
  .al-link { border: 0; background: none; padding: 0; font: inherit; font-weight: 500; color: var(--ae-rose); cursor: pointer; text-underline-offset: 4px; }
  .al-link:hover { text-decoration: underline; }

  .al-ticker { position: relative; overflow: hidden; border-top: 1px solid rgba(255,255,255,0.07); border-bottom: 1px solid rgba(255,255,255,0.07); background: rgba(0,0,0,0.2); padding: 12px 0;
               -webkit-mask-image: linear-gradient(90deg, transparent, #000 22%, #000 78%, transparent); mask-image: linear-gradient(90deg, transparent, #000 22%, #000 78%, transparent); }
  .ae-marquee-track { display: flex; width: max-content; gap: 40px; white-space: nowrap; animation: alMarquee 40s linear infinite; }
  .ae-marquee-track span { display: flex; align-items: center; gap: 40px; font-size: 12px; font-weight: 500; letter-spacing: 0.22em; text-transform: uppercase; color: rgba(255,255,255,0.6); }
  .ae-marquee-track i { width: 4px; height: 4px; border-radius: 50%; background: rgba(229,72,77,0.7); }
  @keyframes alMarquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }

  .al-steps-sec { position: relative; border-top: 1px solid rgba(255,255,255,0.07); flex: 1; }
  .al-steps-glow { position: absolute; left: 0; right: 0; top: 0; height: 320px; pointer-events: none; background: radial-gradient(60% 100% at 50% 0%, rgba(143,42,60,0.25), transparent); }
  .al-steps-in { position: relative; padding-top: 80px; padding-bottom: 80px; }
  @media (min-width: 640px) { .al-steps-in { padding-top: 96px; padding-bottom: 96px; } }
  .al-h2 { margin: 12px 0 0; max-width: 36rem; font-size: clamp(1.6rem, 1.2rem + 1.4vw, 2.4rem); line-height: 1.2; font-weight: 600; letter-spacing: -0.025em; text-wrap: balance; }
  .al-steps { list-style: none; margin: 56px 0 0; padding: 0; display: grid; grid-template-columns: minmax(0,1fr); gap: 40px; }
  @media (min-width: 640px) { .al-steps { grid-template-columns: repeat(2, minmax(0,1fr)); } }
  @media (min-width: 1024px) { .al-steps { grid-template-columns: repeat(5, minmax(0,1fr)); gap: 24px; } }
  .al-step { position: relative; min-width: 0; }
  .al-step .conn { display: none; position: absolute; top: 20px; left: 48px; right: -24px; height: 1px; background: linear-gradient(90deg, rgba(229,72,77,0.5), rgba(229,72,77,0.2)); }
  @media (min-width: 1024px) { .al-step .conn { display: block; } }
  .al-step .ico { position: relative; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; border-radius: 12px; border: 1px solid rgba(255,255,255,0.1);
                  background: #2e1319; color: var(--ae-rose); box-shadow: 0 0 0 6px var(--ae-bg); transition: all .3s; }
  .al-step:hover .ico { background: var(--ae-red); border-color: rgba(229,72,77,0.6); color: #fff; box-shadow: 0 0 0 6px var(--ae-bg), 0 0 30px rgba(229,72,77,0.5); }
  .al-step .num { margin-top: 16px; font-size: 12px; font-weight: 500; color: rgba(255,255,255,0.6); font-variant-numeric: tabular-nums; }
  .al-step h3 { margin: 4px 0 0; font-size: 15px; font-weight: 600; }
  .al-step p { margin: 6px 0 0; font-size: 14px; line-height: 1.6; color: rgba(255,255,255,0.55); }

  .al-ann { border-bottom: 1px solid rgba(251,191,36,0.25); background: rgba(251,191,36,0.08); position: relative; z-index: 20; }
  .al-ann-item { display: flex; gap: 10px; padding: 12px 0; font-size: 13px; line-height: 1.5; }
  .al-ann-item b { display: block; color: #fbbf24; font-weight: 700; margin-bottom: 2px; }
  .al-ann-item span { color: rgba(255,255,255,0.7); }

`;

export default function Landing() {
  const navigate = useNavigate();
  const [showContact, setShowContact] = useState(false);
  const [landingAnnouncements, setLandingAnnouncements] = useState([]);
  useEffect(() => {
    const nowL = new Date();
    getDocs(collection(db, "announcements"))
      .then(snap => {
        const active = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(a => a.active && (a.target === "landing" || a.target === "all") && (!a.expiresAt || new Date(a.expiresAt) > nowL))
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setLandingAnnouncements(active);
      })
      .catch(() => {});
  }, []);

  return (
    <div className="ae">
      <style>{ENTRY_CSS + LANDING_CSS}</style>
      <main style={{ position: "relative", flex: 1, display: "flex", flexDirection: "column" }}>
        <section className="al-hero" aria-labelledby="august-title">
          <MaroonDrift />
          <div className="ae-sweepline" aria-hidden="true"><span /></div>
          <EntryHeader overlay>
            <button className="ae-navlink" onClick={() => navigate("/")}>← <span className="ae-hide-sm">All board </span>meetings</button>
            <button className="ae-navlink" onClick={() => setShowContact(true)}>Contact admin</button>
          </EntryHeader>

          {landingAnnouncements.length > 0 && (
            <div className="al-ann" style={{ position: "absolute", top: 64, left: 0, right: 0 }}>
              <div className="ae-wrap">
                {landingAnnouncements.map(ann => (
                  <div key={ann.id} className="al-ann-item">
                    <span aria-hidden="true">📢</span>
                    <div><b>{ann.title}</b><span>{ann.body}</span></div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="ae-wrap al-grid" style={landingAnnouncements.length ? { paddingTop: 128 + 64 * landingAnnouncements.length } : undefined}>
            <div className="ae-enter" style={{ minWidth: 0 }}>
              <p className="al-pill"><span className="al-dot" aria-hidden="true" />August 2026 · Texas A&amp;M Mays Business School</p>
              <h1 id="august-title" className="al-h1 ae-display">
                <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>August Board Meeting: Trading, Risk &amp; Investments Program</span>
                <span aria-hidden="true">
                  <span className="line">
                    {HEADLINE.map((w, i) => <React.Fragment key={w}><span className="ae-word" style={{ animationDelay: `${120 + i * 70}ms` }}>{w}</span>{" "}</React.Fragment>)}
                  </span>
                  <span className="line">
                    {ACCENT_LINE.map((w, i) => <React.Fragment key={w}><span className="ae-word ae-accent" style={{ animationDelay: `${120 + (HEADLINE.length + i) * 70}ms` }}>{w}</span>{" "}</React.Fragment>)}
                  </span>
                </span>
              </h1>
              <p className="al-lede">The resume book, board week RSVPs and candidate preferences for TRIP students, board members and staff, all in one place.</p>
              <ul className="al-assure">
                {ASSURANCES.map(a => (
                  <li key={a}><span className="al-check"><Icon.Check size={12} /></span>{a}</li>
                ))}
              </ul>
            </div>

            <div className="ae-enter" style={{ minWidth: 0, animationDelay: "250ms" }}>
              <section className="al-panel" aria-labelledby="access-title">
                <div className="al-panel-hair" aria-hidden="true" />
                <div className="al-panel-eyebrow"><Icon.Lock size={14} /> Secure portal</div>
                <h2 id="access-title">Sign in to your portal</h2>
                <p>Choose your access to continue.</p>
                <div className="al-doors">
                  {DOORS.map(({ id, icon: DoorIcon, label, description }) => (
                    <button key={id} type="button" className="al-door" onClick={() => navigate(`/login?as=${id}`)}>
                      <span className="shine" aria-hidden="true" />
                      <span className="ico"><DoorIcon size={18} /></span>
                      <span className="txt"><b>{label}</b><span>{description}</span></span>
                      <Icon.Arrow size={16} className="arr" />
                    </button>
                  ))}
                </div>
                <div className="al-panel-foot">
                  <span>Students, board members and TRIP staff only.</span>
                  <button type="button" className="al-link" onClick={() => setShowContact(true)}>Trouble signing in?</button>
                </div>
              </section>
            </div>
          </div>
        </section>

        {/* Board week as a slow ticker, like a market tape. */}
        <div className="al-ticker" aria-hidden="true">
          <div className="ae-marquee-track">
            {[...TICKER, ...TICKER, ...TICKER, ...TICKER].map((t, i) => <span key={i}>{t}<i /></span>)}
          </div>
        </div>

        <section className="al-steps-sec" aria-labelledby="steps-title">
          <div className="al-steps-glow" aria-hidden="true" />
          <div className="ae-wrap al-steps-in">
            <p className="ae-eyebrow">How board week runs</p>
            <h2 id="steps-title" className="al-h2 ae-display">From student profiles to the interview schedule, in one place.</h2>
            <ol className="al-steps">
              {STEPS.map(({ icon: StepIcon, title, body }, i) => (
                <li key={title} className="al-step">
                  {i < STEPS.length - 1 && <span className="conn" aria-hidden="true" />}
                  <span className="ico"><StepIcon size={18} /></span>
                  <div className="num">0{i + 1}</div>
                  <h3>{title}</h3>
                  <p>{body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>
      </main>
      <EntryFooter />

      <ContactDialog open={showContact} onClose={() => setShowContact(false)} />
    </div>
  );
}
